import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, Phone } from 'lucide-react';
import { BrightLabsIcon } from '../Logo';
import { useDiscovery } from './DiscoveryContext';
import { BRAND, whatsappHref } from '../../lib/brand';

const navLinks = [
  { label: 'Industries', href: '#industries' },
  { label: 'Memberships', href: '#pricing' },
  { label: 'Build Options', href: '#services' },
  { label: 'Client Stories', href: '#client-stories' },
  { label: 'About', href: '/about', external: true },
  { label: 'FAQ', href: '#faq' },
];

export const AgencyNav = ({ anchorPrefix = '' }) => {
  const { openBookCall } = useDiscovery();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isIndia = pathname.startsWith('/india');
  const switcherTo = isIndia ? '/' : '/india';
  const switcherFlag = isIndia ? '🇬🇧' : '🇮🇳';
  const switcherLabel = isIndia ? 'UK' : 'India';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileOpen
          ? 'bg-white/80 backdrop-blur-xl border-b border-zinc-200/80 shadow-[0_1px_12px_rgba(0,0,0,0.04)]'
          : 'bg-transparent border-b border-transparent'
      }`}
      data-testid="agency-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-[68px]">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <BrightLabsIcon className="h-10 w-10 -my-1" />
          <span className="font-heading font-bold text-lg tracking-tight text-zinc-950">BrightLabs</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.external ? l.href : `${anchorPrefix}${l.href}`}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, '-')}`}
              className="text-sm font-medium text-zinc-500 hover:text-zinc-950 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          {isIndia && (
            <Link
              to={switcherTo}
              data-testid="nav-region-switcher"
              aria-label={`Switch to ${switcherLabel} site`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm transition-all text-xs font-bold text-zinc-700"
            >
              <span className="text-sm leading-none">{switcherFlag}</span>
              {switcherLabel} site
            </Link>
          )}
          {isIndia ? (
            <a
              href={whatsappHref(BRAND.indiaWhatsApp, 'Hi BrightLabs, I saw your India site.')}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-link"
              className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              WhatsApp us
            </a>
          ) : (
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="nav-phone-link"
              className="hidden lg:inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-700 hover:text-zinc-950 transition-colors"
            >
              <Phone size={13} className="text-blue-600" />
              {BRAND.phoneDisplay}
            </a>
          )}
          <button
            data-testid="nav-book-call-button"
            onClick={() => openBookCall()}
            className="group bg-zinc-950 text-white hover:bg-zinc-800 transition-colors px-5 py-2.5 rounded-full font-semibold text-sm flex items-center gap-1.5 active:scale-95"
          >
            Book A Call <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>

        <button
          className="md:hidden text-zinc-950 p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white/95 backdrop-blur-xl border-b border-zinc-200 px-6 py-6 space-y-4" data-testid="nav-mobile-menu">
          {navLinks.map((l) => (
            <a key={l.href} href={l.external ? l.href : `${anchorPrefix}${l.href}`} onClick={() => setMobileOpen(false)} className="block text-zinc-700 hover:text-zinc-950 text-base font-medium">
              {l.label}
            </a>
          ))}
          <a
            href={`tel:${BRAND.phoneTel}`}
            data-testid="nav-mobile-phone-link"
            className="flex items-center gap-2 text-zinc-700 text-base font-semibold py-1"
          >
            <Phone size={15} className="text-blue-600" />
            {BRAND.phoneDisplay}
          </a>
          <button
            data-testid="nav-mobile-book-call"
            onClick={() => { setMobileOpen(false); openBookCall(); }}
            className="w-full bg-zinc-950 text-white px-5 py-3 rounded-full font-semibold text-sm"
          >
            Book A Discovery Call
          </button>
          {isIndia && (
            <Link
              to={switcherTo}
              data-testid="nav-mobile-region-switcher"
              onClick={() => setMobileOpen(false)}
              className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-zinc-200 text-zinc-700 text-sm font-bold hover:border-zinc-300"
            >
              <span className="text-base leading-none">{switcherFlag}</span>
              Visit our {switcherLabel} site
            </Link>
          )}
        </div>
      )}
    </header>
  );
};
